// Iter 120 — Public Knowledge Share viewer.
//
// No auth, no tenant context — the recipient sees only the verified
// answer + citations + source PDF links. Tenant/project IDs are never
// rendered because the backend strips them from /api/knowledge-share/{token}.
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  ShieldCheck, ShieldAlert, ExternalLink, Layers, FileText, Calendar,
  Loader2, Archive,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function PublicKnowledgeShare() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);


  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios.get(`${BACKEND_URL}/api/knowledge-share/${token}`)
      .then((r) => {
        if (cancelled) return;
        setData(r.data); setErr(null);
      })
      .catch((e) => {
        if (cancelled) return;
        setErr(e?.response?.data?.detail || "Share not available.");
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [token]);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center text-slate-300">
      <Loader2 size={16} className="animate-spin mr-2" /> Loading shared answer…
    </div>;
  }
  if (err) {
    return <div className="min-h-screen flex items-center justify-center" data-testid="kshare-error">
      <div className="rounded-lg border border-red-500/40 bg-red-500/10 p-6 max-w-md">
        <ShieldAlert size={24} className="text-red-400 mb-3" />
        <h1 className="text-lg font-semibold mb-1">{err}</h1>
        <p className="text-xs text-slate-400">
          The share link may have expired, been revoked, or the originating record was deleted.
        </p>
      </div>
    </div>;
  }
  if (!data) return null;

  const rec = data.record || {};

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <div className="max-w-3xl mx-auto px-5 sm:px-8 py-8 sm:py-12">
        {/* Letterhead */}
        <div className="mb-6 pb-4 border-b border-slate-800">
          <div className="flex items-center gap-2 mb-2">
            <Archive size={14} className="text-orange-400" />
            <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-400">
              ForgedOps Plans™ · Verified knowledge — shared by {data.shared_by || "—"}
            </span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold leading-tight" data-testid="kshare-title">
            {data.title}
          </h1>
          <div className="mt-2 flex items-center gap-3 text-[11px] text-slate-500 font-mono">
            <Calendar size={11} className="inline" />
            <span>Shared {data.shared_at?.slice(0,10)}</span>
            {data.expires_at && <span>· Expires {data.expires_at.slice(0,10)}</span>}
          </div>
        </div>

        {/* Question + answer */}
        <div className="mb-6">
          <div className="text-xs font-mono uppercase tracking-[0.18em] text-slate-500 mb-2">Question</div>
          <p className="text-base mb-5" data-testid="kshare-question">{rec.question}</p>

          <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-5">
            <div className="text-xs font-mono uppercase tracking-[0.18em] text-emerald-300 mb-2 flex items-center gap-1">
              <ShieldCheck size={12} /> Verified answer · {rec.confidence}
            </div>
            <p className="text-[15px] leading-relaxed whitespace-pre-wrap" data-testid="kshare-answer">
              {rec.answer}
            </p>
          </div>
        </div>

        {/* Citations */}
        {(rec.citations || []).length > 0 && (
          <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-4 mb-6" data-testid="kshare-citations">
            <div className="text-xs font-mono uppercase tracking-[0.18em] text-slate-400 mb-3">Why this answer is trusted</div>
            <ul className="space-y-2">
              {rec.citations.map((c, i) => (
                <li key={i} className="text-sm leading-relaxed">
                  <span className="font-mono text-orange-300 mr-2">§{c.section_number}</span>
                  <span className="text-slate-200">{c.section_title}</span>
                  {c.page_start && <span className="text-slate-500 text-xs ml-2">page {c.page_start}</span>}
                  {c.reason && <div className="text-xs text-slate-400 mt-0.5 ml-7">{c.reason}</div>}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Source PDFs */}
        {(rec.sources || []).length > 0 && (
          <>
            <div className="flex items-center gap-2 mt-4 mb-2">
              <FileText size={13} className="text-slate-500" />
              <h2 className="text-xs font-mono uppercase tracking-[0.18em] text-slate-400">
                Source specifications
              </h2>
            </div>
            <div className="space-y-2 mb-6" data-testid="kshare-sources">
              {rec.sources.map((s) => (
                <div key={s.id} className="rounded-lg border border-slate-800 bg-slate-900/40 p-3">
                  <div className="flex items-center gap-2 mb-1">
                    <Badge variant="outline" className="font-mono text-[11px] border-slate-700 text-slate-300">
                      Section {s.section_number}
                    </Badge>
                    {s.page_start && <span className="text-[11px] text-slate-500 font-mono">pages {s.page_start}–{s.page_end || s.page_start}</span>}
                  </div>
                  <div className="text-sm font-medium">{s.section_title}</div>
                  {s.source_document && <div className="text-[11px] text-slate-500 mt-0.5 truncate">{s.source_document}</div>}
                  {s.source_pdf_url && (
                    <a href={s.source_pdf_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 mt-2 text-xs text-orange-300 hover:underline">
                      <ExternalLink size={11} /> Open source PDF · page {s.page_start || 1}
                    </a>
                  )}
                </div>
              ))}
            </div>
          </>
        )}

        {/* Related sheets */}
        {(rec.related?.sheets || []).length > 0 && (
          <>
            <div className="flex items-center gap-2 mt-4 mb-2">
              <Layers size={13} className="text-slate-500" />
              <h2 className="text-xs font-mono uppercase tracking-[0.18em] text-slate-400">
                Related sheets
              </h2>
            </div>
            <div className="space-y-1.5 mb-6">
              {rec.related.sheets.map((s) => (
                <div key={s.id} className="rounded-md border border-slate-800 bg-slate-900/40 px-3 py-2 text-sm">
                  <span className="font-mono text-orange-300 mr-2">{s.sheet_number}</span>
                  <span className="text-slate-200">{s.sheet_title || "—"}</span>
                </div>
              ))}
            </div>
          </>
        )}

        <div className="mt-12 pt-4 border-t border-slate-800 text-[11px] text-slate-500 leading-relaxed">
          This shared record was generated by ForgedOps Plans™ Ask V1. Every section number, page,
          and source link above resolves to a real document in the issuing tenant&apos;s knowledge graph.
          ForgedOps does not generate citations from model memory.
        </div>
      </div>
    </div>
  );
}
